import { z } from 'zod';

/** The longest a single suspension may be set for, in days. */
export const MAX_SUSPENSION_DAYS = 365;

/**
 * A staff-initiated suspension (N9/N20): temporary by construction — `until` is
 * required, so there is no such thing as an indefinite suspension that quietly
 * becomes a ban nobody decided on.
 */
export const SuspendUserSchema = z
  .object({
    until: z.iso.datetime(),
    reason: z.string().min(1).max(1000),
  })
  .strict();

export type SuspendUserRequest = z.infer<typeof SuspendUserSchema>;

/** Lifting a suspension early — the member returns to their pre-suspension status. */
export const RestoreUserSchema = z
  .object({
    reason: z.string().min(1).max(1000).optional(),
  })
  .strict();

export type RestoreUserRequest = z.infer<typeof RestoreUserSchema>;

/** A ban. Terminal, and the reason is mandatory because the member is told it. */
export const BanUserSchema = z
  .object({
    reason: z.string().min(1).max(1000),
  })
  .strict();

export type BanUserRequest = z.infer<typeof BanUserSchema>;

/**
 * The statuses a plain transition may target.
 *
 * `suspended` and `banned` are **excluded deliberately**: they carry
 * consequences a plain status write does not perform — remembering the
 * pre-suspension status, setting an end date, revoking live sessions, emitting
 * the enforcement events P20 listens to. Reaching them through the generic
 * endpoint would produce an indefinite suspension nothing can lift and a ban
 * whose victim keeps a working token. They have their own endpoints.
 */
export const TRANSITIONABLE_USER_STATUSES = [
  'registered',
  'profile_incomplete',
  'pending_verification',
  'approved',
  'rejected',
] as const;

export const TransitionableUserStatusSchema = z.enum(TRANSITIONABLE_USER_STATUSES);

export type TransitionableUserStatus = (typeof TRANSITIONABLE_USER_STATUSES)[number];

/** Any other lifecycle move a staff member may drive directly. */
export const ChangeUserStatusSchema = z
  .object({
    status: TransitionableUserStatusSchema,
    reason: z.string().min(1).max(1000).optional(),
  })
  .strict();

export type ChangeUserStatusRequest = z.infer<typeof ChangeUserStatusSchema>;
